import React from "react";
import Popover from "@mui/material/Popover";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";

/**
 * "QR" button + popover for a public share link. Renders the code locally
 * (qrcode.react, no network) with a "Download PNG" that draws a hidden canvas
 * at print size. Used beside Copy on form links and the swipe deck link.
 *
 * `label` names the download file ("Tavolata - Fremont" -> tavolata_fremont_qr.png).
 */
function QrCodePopover({ url, label, className }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const canvasRef = React.useRef(null);

  if (!url) return null;

  const fileName = `${(label || "link").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase() || "link"}_qr.png`;

  const download = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    try {
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = fileName;
      a.click();
    } catch (e) {
      toast.error("Couldn't download the QR code. Try a screenshot instead.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title="QR code"
        className={
          className ||
          "shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white hover:border-violet-400 text-slate-700 text-xs font-medium px-2.5 py-1"
        }
      >
        <QrCode2Icon style={{ fontSize: 14 }} />
        QR
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { borderRadius: "12px", mt: 1 } } }}
      >
        <div className="p-4 flex flex-col items-center gap-3 w-[260px]">
          <div className="rounded-lg border border-slate-200 p-2 bg-white">
            <QRCodeSVG value={url} size={200} level="M" marginSize={0} />
          </div>
          {label && (
            <div className="text-xs font-medium text-slate-700 text-center truncate w-full">
              {label}
            </div>
          )}
          <div className="text-[11px] text-slate-500 text-center break-all">{url}</div>
          <button
            type="button"
            onClick={download}
            className="w-full rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium py-2"
          >
            Download PNG
          </button>
          {/* Print-size render for the download; never shown. */}
          <div ref={canvasRef} className="hidden" aria-hidden="true">
            <QRCodeCanvas value={url} size={1024} level="M" marginSize={4} />
          </div>
        </div>
      </Popover>
    </>
  );
}

export default QrCodePopover;
